
import { supabase } from '@/integrations/supabase/client';
import { v5 as uuidv5 } from 'uuid';

// A namespace UUID for hashing (randomly generated, but constant)
const NAMESPACE = '1b671a64-40d5-491e-99b0-da01ff1f3341';

/**
 * Tracks anonymous device information
 */
export const trackDeviceInfo = async () => {
  try {
    // Get device information
    const deviceInfo = getDeviceInfo();
    
    // Create a unique hash for this device based on its properties
    // This ensures we don't count the same device multiple times
    // but also keeps the user anonymous
    const deviceHash = generateDeviceHash(deviceInfo);
    
    // Check if this device already exists in the database
    const { data: existingDevice } = await supabase
      .from('user_devices')
      .select('id')
      .eq('device_hash', deviceHash)
      .single();
    
    if (existingDevice) {
      // Update the existing device record (the trigger will increment visit_count)
      await supabase
        .from('user_devices')
        .update({})
        .eq('device_hash', deviceHash);
    } else {
      // Insert a new device record
      await supabase
        .from('user_devices')
        .insert([{
          ...deviceInfo,
          device_hash: deviceHash
        }]);
    }
  } catch (error) {
    console.error('Error tracking device info:', error);
    // Fail silently to not disrupt user experience
  }
};

/**
 * Gets information about the current device
 */
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  // Determine device type
  let deviceType = 'unknown';
  if (/android/i.test(userAgent)) {
    deviceType = 'android';
  } else if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    deviceType = 'ios';
  } else if (/Win/.test(platform)) {
    deviceType = 'windows';
  } else if (/Mac/.test(platform)) {
    deviceType = 'mac';
  } else if (/Linux/.test(platform)) {
    deviceType = 'linux';
  }
  
  // Get browser information
  let browser = 'unknown';
  if (/chrome/i.test(userAgent)) {
    browser = 'chrome';
  } else if (/firefox/i.test(userAgent)) {
    browser = 'firefox';
  } else if (/safari/i.test(userAgent)) {
    browser = 'safari';
  } else if (/edge/i.test(userAgent)) {
    browser = 'edge';
  } else if (/opera/i.test(userAgent) || /opr/i.test(userAgent)) {
    browser = 'opera';
  }
  
  // Get language preference
  const language = navigator.language;
  
  // Try to get country (may not always be available)
  let country = 'unknown';
  try {
    // This is a common approach, but not 100% reliable
    if (navigator.languages && navigator.languages.length > 0) {
      const langParts = navigator.languages[0].split('-');
      if (langParts.length > 1) {
        country = langParts[1];
      }
    }
  } catch (e) {
    console.error('Error detecting country:', e);
  }
  
  // Get OS information
  let operatingSystem = 'unknown';
  if (/Windows NT 10.0/.test(userAgent)) {
    operatingSystem = 'Windows 10';
  } else if (/Windows NT 6.3/.test(userAgent)) {
    operatingSystem = 'Windows 8.1';
  } else if (/iPhone OS/.test(userAgent)) {
    const match = userAgent.match(/iPhone OS ([0-9_]+)/);
    if (match && match[1]) {
      operatingSystem = `iOS ${match[1].replace('_', '.')}`;
    } else {
      operatingSystem = 'iOS';
    }
  } else if (/Android/.test(userAgent)) {
    const match = userAgent.match(/Android ([0-9.]+)/);
    if (match && match[1]) {
      operatingSystem = `Android ${match[1]}`;
    } else {
      operatingSystem = 'Android';
    }
  } else if (/Mac OS X/.test(userAgent)) {
    operatingSystem = 'macOS';
  } else if (/Linux/.test(userAgent)) {
    operatingSystem = 'Linux';
  }
  
  // Get device model (mostly reliable for mobile)
  let deviceModel = 'unknown';
  if (/iPhone/.test(userAgent)) {
    deviceModel = 'iPhone';
  } else if (/iPad/.test(userAgent)) {
    deviceModel = 'iPad';
  } else if (/Android/.test(userAgent)) {
    // Try to extract Android device model - this is not 100% reliable
    const match = userAgent.match(/\((.+?)\)/);
    if (match && match[1]) {
      const parts = match[1].split(';');
      if (parts.length > 2) {
        deviceModel = parts[2].trim();
      }
    }
  }
  
  // Get screen size
  const screenSize = `${window.screen.width}x${window.screen.height}`;
  
  return {
    device_type: deviceType,
    device_model: deviceModel,
    operating_system: operatingSystem,
    browser: browser,
    screen_size: screenSize,
    language: language,
    country: country
  };
};

/**
 * Generates a unique hash for device identification without storing PII
 */
const generateDeviceHash = (deviceInfo: any) => {
  // Combine various device properties to create a unique identifier
  const deviceString = `${deviceInfo.device_type}|${deviceInfo.device_model}|${deviceInfo.operating_system}|${deviceInfo.browser}|${deviceInfo.screen_size}|${navigator.userAgent}`;
  
  // Hash the device string using UUID v5
  return uuidv5(deviceString, NAMESPACE);
};
